import { WHATS_NEW_STORAGE_KEY } from "constants/whats-new.constants";
import type { WhatsNewRelease } from "models/whats-new";
import { useUserInfoContext } from "providers/UserInfoProvider";
import { useCallback } from "react";
import { useLocalStorage } from "usehooks-ts";
import type { AccessLevel } from "./use-user-info";
import { useWhatsNewRelease } from "./whats-new";

/**
 * Pure gate that decides whether a release should surface. Audience is gated
 * server-side (the endpoint returns 204 for an out-of-audience caller, which
 * arrives here as a null release), so this only enforces the checks the backend
 * cannot: the release has not been dismissed, and the viewer is not DEMO/BLOCKED.
 *
 * @param params.release - the fetched release, or null when none applies (204)
 * @param params.accessLevel - the viewer's access level (DEMO/BLOCKED excluded)
 * @param params.lastSeenReleaseId - the releaseId the viewer last dismissed
 * @returns true only when there is a release, it has not been seen, and the
 *   access level is neither DEMO nor BLOCKED
 */
export function shouldShowRelease(params: {
  release: WhatsNewRelease | null;
  accessLevel: AccessLevel | undefined;
  lastSeenReleaseId: string | null;
}): boolean {
  const { release, accessLevel, lastSeenReleaseId } = params;
  if (!release) return false;
  if (lastSeenReleaseId === release.releaseId) return false;
  if (accessLevel === "DEMO" || accessLevel === "BLOCKED") return false;
  return true;
}

/**
 * Composes the "What's New" popup visibility from onboarding-gate state, the
 * viewer's access level, and the last-seen release in localStorage. The release
 * itself is fetched from the backend (CX-3628/CX-3629) via
 * {@link useWhatsNewRelease}; a 204 response (empty body) is treated as "no
 * release" so the popup stays closed.
 *
 * @returns `release` (null until loaded or on 204), `isOpen` (whether the popup
 *   should show now), and `markSeen` (persists the releaseId so it won't reappear)
 */
export function useWhatsNew(): {
  release: WhatsNewRelease | null;
  isOpen: boolean;
  markSeen: () => void;
} {
  const { userInfo, gateState } = useUserInfoContext();
  const [lastSeenReleaseId, setLastSeenReleaseId] = useLocalStorage<
    string | null
  >(WHATS_NEW_STORAGE_KEY, null);

  // Only surface once the onboarding gate has settled the user into the app.
  // A blocked user is redirected to /access-denied but the controller still
  // mounts, so gate them out here; checking/onboarding show a loader; error is
  // a broken session. "idle" is the settled state for users whose onboarding
  // gate never runs (internal/Azure-SSO users, and externals without a cdcUser)
  // — they are legitimately in the app, so the popup must still reach them.
  const gateSettled = gateState === "idle" || gateState === "ready";
  const accessLevel = userInfo?.accessLevel;
  const shouldLoad =
    gateSettled && accessLevel !== "DEMO" && accessLevel !== "BLOCKED";

  const [fetched, , isLoading] = useWhatsNewRelease(shouldLoad);
  // A 204 leaves axios `data` as an empty string; anything that is not a
  // release object counts as "no release".
  const release: WhatsNewRelease | null =
    fetched && typeof fetched === "object" ? fetched : null;

  const isOpen =
    gateSettled &&
    !isLoading &&
    shouldShowRelease({ release, accessLevel, lastSeenReleaseId });

  const markSeen = useCallback(() => {
    if (release) setLastSeenReleaseId(release.releaseId);
  }, [release, setLastSeenReleaseId]);

  return { release, isOpen, markSeen };
}
